/**
 * Static time-distance diagram as inline SVG.
 *
 * Drawn from the same corridor numbers the tool would load, so the learn
 * pages carry a real, crawlable, dark-themed diagram at a few kilobytes
 * instead of a screenshot. Conventions match the workspace canvas:
 * distance up the page, time across, one phase bar per intersection shifted
 * by its offset, cyan = both directions, purple = northbound only,
 * green = southbound only, grey = not in progression, and a translucent
 * band for the northbound design-speed trajectory that leaves the first
 * intersection's green.
 */

import {
  AMBER_S,
  cumulativeDistance,
  cycleLength,
  greenWindows,
  kmhToMs,
  type ExampleIntersection,
} from './corridor-example';

interface TimeDistanceFigureProps {
  corridor: ExampleIntersection[];
  /** Seconds of time axis to draw (default: two cycles) */
  seconds?: number;
  /** Draw the northbound progression band from the first intersection's green */
  showBand?: boolean;
  /** Override offsets (for the "before" state) */
  offsets?: number[];
  title: string;
  description: string;
}

const COLOR: Record<string, string> = {
  NS: '#22c5d8',
  N: '#a855f7',
  S: '#22c55e',
  NA: '#64748b',
};

const W = 760;
const H = 420;
const PAD = { left: 64, right: 24, top: 28, bottom: 44 };

export default function TimeDistanceFigure({
  corridor,
  seconds,
  showBand = true,
  offsets,
  title,
  description,
}: TimeDistanceFigureProps) {
  const cycle = cycleLength(corridor[0]);
  const span = seconds ?? cycle * 2;
  const dist = cumulativeDistance(corridor);
  const length = dist[dist.length - 1];
  const plotW = W - PAD.left - PAD.right;
  const plotH = H - PAD.top - PAD.bottom;
  const x = (t: number) => PAD.left + (t / span) * plotW;
  const y = (d: number) => PAD.top + plotH - (d / length) * plotH;
  const barH = 14;

  const offsetOf = (i: number) => offsets?.[i] ?? corridor[i].offset;

  // Northbound band: departures during the first intersection's first green
  // run, projected at each intersection's own northbound design speed.
  const band: string[] = [];
  if (showBand) {
    const first = greenWindows(corridor[0], 'N')[0];
    if (first) {
      const startDepart = offsetOf(0) + first.start;
      const endDepart = offsetOf(0) + first.end;
      const left: Array<[number, number]> = [];
      const right: Array<[number, number]> = [];
      let travel = 0;
      corridor.forEach((_int, i) => {
        if (i > 0) travel += corridor[i - 1].distanceToNext / kmhToMs(corridor[i - 1].speedNorth);
        left.push([startDepart + travel, dist[i]]);
        right.push([endDepart + travel, dist[i]]);
      });
      const points = [...left, ...right.reverse()].map(([t, d]) => `${x(t).toFixed(1)},${y(d).toFixed(1)}`);
      band.push(points.join(' '));
    }
  }

  const timeTicks: number[] = [];
  for (let t = 0; t <= span; t += cycle / 4) timeTicks.push(Math.round(t));
  const distTicks: number[] = [];
  const stepD = length > 1500 ? 500 : 250;
  for (let d = 0; d <= length; d += stepD) distTicks.push(d);

  return (
    <svg
      className="td-figure"
      viewBox={`0 0 ${W} ${H}`}
      role="img"
      aria-labelledby="td-figure-title td-figure-desc"
      width={W}
      height={H}
    >
      <title id="td-figure-title">{title}</title>
      <desc id="td-figure-desc">{description}</desc>
      <rect x="0" y="0" width={W} height={H} fill="#020817" rx="10" />

      {/* Grid */}
      {timeTicks.map((t) => (
        <line key={`gt${t}`} x1={x(t)} x2={x(t)} y1={PAD.top} y2={PAD.top + plotH} stroke="#1e293b" strokeWidth="1" />
      ))}
      {distTicks.map((d) => (
        <line key={`gd${d}`} x1={PAD.left} x2={PAD.left + plotW} y1={y(d)} y2={y(d)} stroke="#1e293b" strokeWidth="1" />
      ))}

      {/* Band */}
      {band.map((pts, i) => (
        <polygon key={i} points={pts} fill="rgba(34, 68, 204, 0.32)" stroke="rgba(96, 165, 250, 0.7)" strokeWidth="1" />
      ))}

      {/* Phase bars */}
      {corridor.map((int, i) => {
        const cy = y(dist[i]);
        const segs: React.ReactNode[] = [];
        const off = offsetOf(i);
        // Draw repeating cycles that intersect the window
        const firstCycle = Math.floor((0 - off) / cycle) - 1;
        const lastCycle = Math.ceil((span - off) / cycle) + 1;
        for (let c = firstCycle; c <= lastCycle; c += 1) {
          let t = off + c * cycle;
          for (const p of int.phases) {
            const t0 = Math.max(0, t);
            const t1 = Math.min(span, t + p.duration);
            if (t1 > t0) {
              const amberStart = Math.max(t0, Math.min(t1, t + p.duration - AMBER_S));
              segs.push(
                <rect key={`${c}${p.id}g`} x={x(t0)} y={cy - barH / 2} width={x(amberStart) - x(t0)} height={barH} fill={COLOR[p.direction]} />,
              );
              if (t1 > amberStart) {
                segs.push(
                  <rect key={`${c}${p.id}a`} x={x(amberStart)} y={cy - barH / 2} width={x(t1) - x(amberStart)} height={barH} fill="#f59e0b" />,
                );
              }
              if (t1 - t0 > cycle * 0.06) {
                segs.push(
                  <text key={`${c}${p.id}l`} x={(x(t0) + x(amberStart)) / 2} y={cy + 4} fontSize="9" fill="#0b1020" textAnchor="middle" fontFamily="ui-monospace, monospace">
                    {p.id}
                  </text>,
                );
              }
            }
            t += p.duration;
          }
        }
        return (
          <g key={int.no}>
            {segs}
            <text x={PAD.left + plotW - 4} y={cy - barH / 2 - 4} fontSize="11" fill="#fbbf24" textAnchor="end" fontFamily="ui-monospace, monospace">
              #{int.no} {int.name.replace(/^.*& /, '')} · offset {off}s
            </text>
          </g>
        );
      })}

      {/* Axes */}
      <line x1={PAD.left} x2={PAD.left} y1={PAD.top} y2={PAD.top + plotH} stroke="#475569" />
      <line x1={PAD.left} x2={PAD.left + plotW} y1={PAD.top + plotH} y2={PAD.top + plotH} stroke="#475569" />
      {timeTicks.map((t) => (
        <text key={`tt${t}`} x={x(t)} y={PAD.top + plotH + 16} fontSize="10" fill="#94a3b8" textAnchor="middle" fontFamily="ui-monospace, monospace">
          {t}
        </text>
      ))}
      {distTicks.map((d) => (
        <text key={`dt${d}`} x={PAD.left - 8} y={y(d) + 3} fontSize="10" fill="#94a3b8" textAnchor="end" fontFamily="ui-monospace, monospace">
          {d}
        </text>
      ))}
      <text x={PAD.left + plotW / 2} y={H - 8} fontSize="11" fill="#cbd5e1" textAnchor="middle">Time (seconds)</text>
      <text x={14} y={PAD.top + plotH / 2} fontSize="11" fill="#cbd5e1" textAnchor="middle" transform={`rotate(-90 14 ${PAD.top + plotH / 2})`}>
        Distance north (m)
      </text>
    </svg>
  );
}
